import React from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';

import type { IdAccountDto } from '@/services/ws-crm/src/main/resources/rest-api';
import type { Role } from '@/services/ws-orders/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Filter } from '@/js/libs/ui/src/V2';

import { useMarkedOptionLabel } from 'src/hooks/useMarkedOptionLabel';
import type { Option } from 'src/types';
import { useAccountsByRoleOptions } from '../hooks/useAccountsByRoleOptions';

interface AccountByRoleFilterProps {
  value: Option<IdAccountDto> | null;
  onChange: (value: SingleValue<Option<IdAccountDto>>) => void;
  label?: string;
  role?: Role;
  dataTestId?: string;
}

export const AccountByRoleFilter: React.FC<AccountByRoleFilterProps> = React.memo(function AccountByRoleFilter({
  value,
  onChange,
  label,
  role,
  dataTestId,
}) {
  const [t] = useTranslation(['backend', 'common']);
  const formatOptionLabel = useMarkedOptionLabel({ t });
  const { options, pending: loading, total, searchFilter, setSearchFilter } = useAccountsByRoleOptions(role);

  const handleChange = useCallbackRef((newValue: SingleValue<Option<IdAccountDto>>) => {
    onChange(newValue);
  });

  return (
    <Filter<IdAccountDto>
      label={label || (role ? t(role) : t('common:ACCOUNT'))}
      loading={loading}
      selected={value}
      onChange={handleChange}
      serverSideFiltering={{
        filter: searchFilter,
        setFilter: setSearchFilter,
        total,
      }}
      options={options}
      formatOptionLabel={formatOptionLabel}
      dataTestId={dataTestId}
    />
  );
});
